import {
	LoginStatusFilter,
	MfaBypassFilter,
	OrderTypes,
	PremiumStatusFilter,
	SignupStatusFilter,
	useApproveManyPremiumAccessMutation,
	useApprovePremiumAccessMutation,
	useBypassManyMigrated2FaMutation,
	useBypassMigrated2FaMutation,
	useDeleteManyUserMutation,
	useDeleteUserMutation,
	useGetAllUsersLazyQuery,
	useGetAllUsersQuery,
	useRejectManyPremiumAccessMutation,
	useRejectPremiumAccessMutation,
} from '@codezo/graphql';

type StatusFilter = 'ALL' | 'AWAITING_VALIDATION' | 'REQUESTED_PREMIUM';
type LoginFilter = 'ALL' | 'NOT_GIVEN_ACCESS' | 'AWAITING_LOGIN' | 'HAS_LOGGED_IN';
import { DataContainer, formatDateForTable } from '@codezo/ui';
import {
	ActionIcon,
	Avatar,
	Badge,
	Button,
	Checkbox,
	Group,
	Menu,
	Modal,
	Pagination,
	Paper,
	Select,
	Stack,
	Table,
	Text,
	TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
	IconCheck,
	IconChevronDown,
	IconChevronUp,
	IconCrown,
	IconDots,
	IconKey,
	IconSearch,
	IconTrash,
	IconDownload,
	IconUsers,
	IconX,
} from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';

const sortableHeaderStyles = `
	.sortable-header .sort-icon-wrapper {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		margin-left: 4px;
		vertical-align: middle;
	}
	.sortable-header .sort-icon-hover {
		opacity: 0;
		transition: opacity 0.15s ease;
		display: inline-flex;
		align-items: center;
	}
	.sortable-header:hover .sort-icon-hover {
		opacity: 1;
	}
`;

export function RegularUsersTable() {
	// Search by email is supported via federated RPC to authentication service
	const [searchValue, setSearchValue] = useState('');
	const [debouncedSearch] = useDebouncedValue(searchValue, 300);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
	const [loginFilter, setLoginFilter] = useState<LoginFilter>('ALL');
	const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [exporting, setExporting] = useState(false);
	const [sortField, setSortField] = useState<string>('createdAt');
	const [sortDirection, setSortDirection] = useState<OrderTypes>(OrderTypes.Desc);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);

	// Derive backend filters from the two dropdowns
	const derivedFilters = useMemo(() => {
		let signupStatusFilter = SignupStatusFilter.All;
		let premiumStatusFilter = PremiumStatusFilter.All;
		let loginStatusFilter = LoginStatusFilter.All;
		let mfaBypassFilter = MfaBypassFilter.All;

		// Status dropdown (client-facing)
		switch (statusFilter) {
			case 'AWAITING_VALIDATION':
				signupStatusFilter = SignupStatusFilter.Pending;
				mfaBypassFilter = MfaBypassFilter.NotBypassed;
				break;
			case 'REQUESTED_PREMIUM':
				premiumStatusFilter = PremiumStatusFilter.Pending;
				break;
		}

		// Login dropdown (debugging)
		switch (loginFilter) {
			case 'NOT_GIVEN_ACCESS':
				loginStatusFilter = LoginStatusFilter.NeverLoggedIn;
				mfaBypassFilter = MfaBypassFilter.NotBypassed;
				break;
			case 'AWAITING_LOGIN':
				loginStatusFilter = LoginStatusFilter.NeverLoggedIn;
				mfaBypassFilter = MfaBypassFilter.Bypassed;
				break;
			case 'HAS_LOGGED_IN':
				loginStatusFilter = LoginStatusFilter.HasLoggedIn;
				break;
		}

		return { signupStatusFilter, premiumStatusFilter, loginStatusFilter, mfaBypassFilter };
	}, [statusFilter, loginFilter]);

	// Query for regular users via User service (uses federated search for email)
	const { data, loading, error, refetch } = useGetAllUsersQuery({
		variables: {
			input: {
				pagination: { currentPage, limit: pageSize },
				order: { field: sortField, direction: sortDirection },
				search: debouncedSearch || undefined,
				premiumStatusFilter: derivedFilters.premiumStatusFilter,
				signupStatusFilter: derivedFilters.signupStatusFilter,
				loginStatusFilter: derivedFilters.loginStatusFilter,
				mfaBypassFilter: derivedFilters.mfaBypassFilter,
			},
		},
		skip: false,
		errorPolicy: 'all',
		fetchPolicy: 'network-only',
	});

	const [fetchAllUsers] = useGetAllUsersLazyQuery({ fetchPolicy: 'network-only' });

	const handleExportCsv = useCallback(async () => {
		setExporting(true);
		try {
			const allUsers: { email: string; firstName: string; lastName: string }[] = [];
			let page = 1;
			let totalPages = 1;

			while (page <= totalPages) {
				const { data: pageData } = await fetchAllUsers({
					variables: {
						input: {
							pagination: { currentPage: page, limit: 100 },
							order: { field: 'createdAt', direction: OrderTypes.Desc },
						},
					},
				});

				if (pageData?.getAllUsers) {
					for (const user of pageData.getAllUsers.data) {
						allUsers.push({
							email: (user.authenticationUser?.email || '').toLowerCase().trim(),
							firstName: user.firstName || '',
							lastName: user.lastName || '',
						});
					}
					totalPages = pageData.getAllUsers.pageInfo.totalPages;
				}
				page++;
			}

			// Build CSV formatted for newsletter subscriber import
			const csvRows = ['email,first_name,last_name'];
			for (const u of allUsers) {
				if (!u.email) continue;
				const escape = (v: string) => (v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v);
				csvRows.push(`${escape(u.email)},${escape(u.firstName)},${escape(u.lastName)}`);
			}

			const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `regular-users-export-${new Date().toISOString().slice(0, 10)}.csv`;
			a.click();
			setTimeout(() => URL.revokeObjectURL(url), 3000);

			notifications.show({
				title: 'Export Complete',
				message: `Exported ${allUsers.filter(u => u.email).length} users`,
				color: 'green',
			});
		} catch (err) {
			notifications.show({
				title: 'Export Failed',
				message: err instanceof Error ? err.message : 'Failed to export users',
				color: 'red',
			});
		} finally {
			setExporting(false);
		}
	}, [fetchAllUsers]);

	// Premium access mutations
	const [approvePremiumAccess] = useApprovePremiumAccessMutation({
		onCompleted: () => {
			notifications.show({
				title: 'Premium Access Approved',
				message: 'User has been granted premium access',
				color: 'green',
			});
			refetch();
		},
		onError: (err) => {
			notifications.show({
				title: 'Approval Failed',
				message: err.message || 'Failed to approve premium access',
				color: 'red',
			});
		},
	});

	const [rejectPremiumAccess] = useRejectPremiumAccessMutation({
		onCompleted: () => {
			notifications.show({
				title: 'Premium Access Rejected',
				message: 'User premium access request has been rejected',
				color: 'blue',
			});
			refetch();
		},
		onError: (err) => {
			notifications.show({
				title: 'Rejection Failed',
				message: err.message || 'Failed to reject premium access',
				color: 'red',
			});
		},
	});

	// Bulk premium mutations
	const [approveManyPremiumAccess] = useApproveManyPremiumAccessMutation({
		onCompleted: (result) => {
			if (result.approveManyPremiumAccess.success) {
				notifications.show({
					title: 'Premium Access Approved',
					message: result.approveManyPremiumAccess.message || `Successfully approved ${selectedUsers.length} user(s)`,
					color: 'green',
				});
				setSelectedUsers([]);
				refetch();
			}
		},
		onError: (err) => {
			notifications.show({
				title: 'Bulk Approval Failed',
				message: err.message || 'Failed to approve premium access',
				color: 'red',
			});
		},
	});

	const [rejectManyPremiumAccess] = useRejectManyPremiumAccessMutation({
		onCompleted: (result) => {
			if (result.rejectManyPremiumAccess.success) {
				notifications.show({
					title: 'Premium Access Rejected',
					message: result.rejectManyPremiumAccess.message || `Successfully rejected ${selectedUsers.length} user(s)`,
					color: 'blue',
				});
				setSelectedUsers([]);
				refetch();
			}
		},
		onError: (err) => {
			notifications.show({
				title: 'Bulk Rejection Failed',
				message: err.message || 'Failed to reject premium access',
				color: 'red',
			});
		},
	});

	// Bypass 2FA mutation
	const [bypassMigrated2FA] = useBypassMigrated2FaMutation({
		onCompleted: (result) => {
			if (result.bypassMigrated2FA.success) {
				notifications.show({
					title: '2FA Bypassed',
					message: 'User can now enter any code to proceed',
					color: 'green',
				});
				refetch();
			}
		},
		onError: (err) => {
			notifications.show({
				title: 'Bypass Failed',
				message: err.message || 'Failed to bypass 2FA',
				color: 'red',
			});
		},
	});

	// Bulk bypass 2FA mutation
	const [bypassManyMigrated2FA] = useBypassManyMigrated2FaMutation({
		onCompleted: (result) => {
			if (result.bypassManyMigrated2FA.success) {
				notifications.show({
					title: '2FA Bypassed',
					message: result.bypassManyMigrated2FA.message || `Successfully bypassed 2FA for ${selectedUsers.length} user(s)`,
					color: 'green',
				});
				setSelectedUsers([]);
				refetch();
			}
		},
		onError: (err) => {
			notifications.show({
				title: 'Bulk Bypass Failed',
				message: err.message || 'Failed to bypass 2FA',
				color: 'red',
			});
		},
	});

	// Delete mutations
	const [deleteUser] = useDeleteUserMutation();
	const [deleteUsers] = useDeleteManyUserMutation({
		onCompleted: (result) => {
			if (result.deleteManyUser.success) {
				notifications.show({
					title: 'Users Deleted',
					message: `Successfully deleted ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}`,
					color: 'green',
				});
				setSelectedUsers([]);
				setDeleteModalOpen(false);
				refetch();
			} else {
				notifications.show({
					title: 'Delete Failed',
					message: result.deleteManyUser.message || 'Failed to delete users',
					color: 'red',
				});
			}
		},
		onError: (err) => {
			notifications.show({
				title: 'Delete Failed',
				message: err.message || 'An error occurred while deleting users',
				color: 'red',
			});
		},
	});

	const users = data?.getAllUsers?.data || [];
	const filteredUsers = useMemo(() => users, [users]);

	const selectAll = () => {
		if (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
			setSelectedUsers([]);
		} else {
			setSelectedUsers(
				filteredUsers.map((user: { authenticationUser?: { id: string } | null }) => user.authenticationUser?.id).filter(Boolean) as string[]
			);
		}
	};

	const confirmDelete = async () => {
		try {
			if (deletingId) {
				// Single delete
				const result = await deleteUser({
					variables: { id: deletingId },
				});
				if (result.data?.deleteUser?.success) {
					notifications.show({
						title: 'User Deleted',
						message: 'Successfully deleted user',
						color: 'green',
					});
					setDeleteModalOpen(false);
					setDeletingId(null);
					refetch();
				} else {
					notifications.show({
						title: 'Delete Failed',
						message: result.data?.deleteUser?.message || 'Failed to delete user',
						color: 'red',
					});
				}
			} else if (selectedUsers.length > 0) {
				// Bulk delete
				await deleteUsers({
					variables: {
						input: {
							ids: selectedUsers,
						},
					},
				});
			}
		} catch {
			notifications.show({
				title: 'Delete Failed',
				message: 'An error occurred while deleting user(s)',
				color: 'red',
			});
		}
	};

	const isAllSelected = selectedUsers.length === filteredUsers.length && filteredUsers.length > 0;
	const isIndeterminate = selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length;

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === OrderTypes.Asc ? OrderTypes.Desc : OrderTypes.Asc);
		} else {
			setSortField(field);
			setSortDirection(OrderTypes.Asc);
		}
		setCurrentPage(1);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		setSelectedUsers([]); // Clear selection when changing pages
	};

	const getSortIcon = (field: string) => {
		if (sortField !== field) {
			return (
				<span className="sort-icon-wrapper">
					<span className="sort-icon-hover">
						<IconChevronDown size={14} />
					</span>
				</span>
			);
		}
		return (
			<span className="sort-icon-wrapper">
				<span
					style={{
						background: 'var(--mantine-color-gray-1)',
						borderRadius: '4px',
						padding: '2px',
						display: 'inline-flex',
						alignItems: 'center',
					}}
				>
					{sortDirection === OrderTypes.Asc ? (
						<IconChevronUp size={14} />
					) : (
						<IconChevronDown size={14} />
					)}
				</span>
			</span>
		);
	};

	return (
		<>
			<style>{sortableHeaderStyles}</style>
			<Stack gap="md">
				{/* Header row */}
				<Group justify="space-between">
					<div>
						<Text size="lg" fw={600}>
							Regular Users
						</Text>
						<Text size="sm" c="dimmed">
							Manage premium access for regular users
						</Text>
					</div>
					<Group gap="sm">
						<Button
							variant="default"
							leftSection={<IconDownload size={16} />}
							onClick={handleExportCsv}
							loading={exporting}
						>
							Export CSV
						</Button>
					</Group>
				</Group>

				{/* Search + filters row */}
				<Group gap="sm">
					<TextInput
						placeholder="Search by email..."
						leftSection={<IconSearch size={16} />}
						value={searchValue}
						onChange={(e) => {
							setSearchValue(e.currentTarget.value);
							setCurrentPage(1);
						}}
						style={{ flex: 1, maxWidth: 300 }}
					/>
					<Select
						data={[
							{ value: 'ALL', label: 'Alle brugere' },
							{ value: 'AWAITING_VALIDATION', label: 'Afventer validering' },
							{ value: 'REQUESTED_PREMIUM', label: 'Anmodet om premium' },
						]}
						value={statusFilter}
						onChange={(value) => {
							setStatusFilter((value as StatusFilter) || 'ALL');
							setCurrentPage(1);
						}}
						w={210}
					/>
					<Select
						data={[
							{ value: 'ALL', label: 'Alle' },
							{ value: 'NOT_GIVEN_ACCESS', label: 'Ikke givet adgang' },
							{ value: 'AWAITING_LOGIN', label: 'Afventer login' },
							{ value: 'HAS_LOGGED_IN', label: 'Har logget ind' },
						]}
						value={loginFilter}
						onChange={(value) => {
							setLoginFilter((value as LoginFilter) || 'ALL');
							setCurrentPage(1);
						}}
						w={180}
					/>
				</Group>

				{/* Bulk actions bar - only visible when users are selected */}
				{selectedUsers.length > 0 && (
					<Paper withBorder p="xs" radius="md" bg="gray.0">
						<Group justify="space-between">
							<Text size="sm" fw={500} c="dimmed">
								{selectedUsers.length} bruger{selectedUsers.length > 1 ? 'e' : ''} valgt
							</Text>
							<Group gap="sm">
								<Button
									size="xs"
									leftSection={<IconCheck size={14} />}
									color="green"
									variant="light"
									onClick={() => {
										approveManyPremiumAccess({ variables: { userIds: selectedUsers } });
									}}
								>
									Godkend premium
								</Button>
								<Button
									size="xs"
									leftSection={<IconX size={14} />}
									color="orange"
									variant="light"
									onClick={() => {
										rejectManyPremiumAccess({ variables: { userIds: selectedUsers } });
									}}
								>
									Afvis premium
								</Button>
								<Button
									size="xs"
									leftSection={<IconKey size={14} />}
									color="dark"
									variant="light"
									onClick={() => {
										bypassManyMigrated2FA({ variables: { userIds: selectedUsers } });
									}}
								>
									Giv 2FA adgang
								</Button>
								<Button
									size="xs"
									leftSection={<IconTrash size={14} />}
									color="red"
									variant="light"
									onClick={() => {
										setDeleteModalOpen(true);
										setDeletingId(null);
									}}
								>
									Slet
								</Button>
								<Button
									size="xs"
									variant="subtle"
									color="gray"
									onClick={() => setSelectedUsers([])}
								>
									Ryd valg
								</Button>
							</Group>
						</Group>
					</Paper>
				)}

				<DataContainer loading={loading} error={error}>
					<Paper p="lg" withBorder style={{ overflow: 'hidden' }}>
						<Table.ScrollContainer minWidth={800}>
							<Table verticalSpacing="sm" highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<Table.Th w={40}>
											<Checkbox
												checked={isAllSelected}
												indeterminate={isIndeterminate}
												onChange={selectAll}
											/>
										</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('firstName')}
										>
											<Group gap={4} wrap="nowrap">
												Name
												{getSortIcon('firstName')}
											</Group>
										</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('email')}
										>
											<Group gap={4} wrap="nowrap">
												Email
												{getSortIcon('email')}
											</Group>
										</Table.Th>
										<Table.Th>Signup</Table.Th>
										<Table.Th>Premium</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('lastActive')}
										>
											<Group gap={4} wrap="nowrap">
												Last Active
												{getSortIcon('lastActive')}
											</Group>
										</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('createdAt')}
										>
											<Group gap={4} wrap="nowrap">
												Created
												{getSortIcon('createdAt')}
											</Group>
										</Table.Th>
										<Table.Th w={80}>Actions</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{users.length === 0 ? (
										<Table.Tr>
											<Table.Td colSpan={8}>
												<Stack align="center" gap="xl" py={60}>
													<IconUsers size={80} stroke={0.8} />
													<Stack align="center" gap="sm">
														<Text size="lg" fw={600} c="gray.7">
															No users found
														</Text>
														<Text
															size="sm"
															c="dimmed"
															ta="center"
															maw={350}
														>
															{searchValue || statusFilter !== 'ALL' || loginFilter !== 'ALL'
																? 'No users match your search or filter criteria.'
																: 'No regular users found in the system.'}
														</Text>
													</Stack>
												</Stack>
											</Table.Td>
										</Table.Tr>
									) : (
										users.map((user: any) => (
											<Table.Tr key={user.id}>
												<Table.Td>
													<Checkbox
														checked={selectedUsers.includes(user.authenticationUser?.id || '')}
														onChange={() => {
															const authUserId = user.authenticationUser?.id;
															if (!authUserId) return;

															if (selectedUsers.includes(authUserId)) {
																setSelectedUsers(selectedUsers.filter((id) => id !== authUserId));
															} else {
																setSelectedUsers([...selectedUsers, authUserId]);
															}
														}}
													/>
												</Table.Td>
												<Table.Td>
													<Group gap="sm">
														<Avatar size="sm" radius="md">
															{user.firstName?.charAt(0)}
															{user.lastName?.charAt(0)}
														</Avatar>
														<Text>
															{user.firstName} {user.lastName}
														</Text>
													</Group>
												</Table.Td>
												<Table.Td>
													<Text c="dimmed">
														{user.authenticationUser?.email || 'No email'}
													</Text>
												</Table.Td>
												<Table.Td>
													{user.authenticationUser?.status === 'PENDING' ? (
														<Badge color="orange" variant="light">
															Pending
														</Badge>
													) : (
														<Badge color="green" variant="light">
															Verified
														</Badge>
													)}
												</Table.Td>
												<Table.Td>
													{user.authenticationUser?.isPremium ? (
														<Badge color="green" variant="light" leftSection={<IconCrown size={12} />}>
															Premium
														</Badge>
													) : user.authenticationUser?.premiumRequestedAt && !user.authenticationUser?.premiumApprovedAt ? (
														<Badge color="yellow" variant="light">
															Pending
														</Badge>
													) : (
														<Badge color="gray" variant="light">
															Limited
														</Badge>
													)}
												</Table.Td>
												<Table.Td>
													<Text c="dimmed">
														{user.authenticationUser?.lastActive
															? formatDateForTable(user.authenticationUser.lastActive)
															: '-'}
													</Text>
												</Table.Td>
												<Table.Td>
													<Text c="dimmed">
														{user.createdAt
															? formatDateForTable(user.createdAt)
															: '-'}
													</Text>
												</Table.Td>
												<Table.Td>
													<Menu position="bottom-end" withinPortal>
														<Menu.Target>
															<ActionIcon
																variant="subtle"
																color="gray"
															>
																<IconDots size={16} />
															</ActionIcon>
														</Menu.Target>
														<Menu.Dropdown>
															<Menu.Label>Premium Access</Menu.Label>
															{user.authenticationUser?.premiumRequestedAt &&
															 !user.authenticationUser?.premiumApprovedAt &&
															 !user.authenticationUser?.isPremium && (
																<>
																	<Menu.Item
																		color="green"
																		leftSection={<IconCheck size={14} />}
																		onClick={(e) => {
																			e.stopPropagation();
																			if (user.authenticationUser?.id) {
																				approvePremiumAccess({
																					variables: { userId: user.authenticationUser.id },
																				});
																			}
																		}}
																	>
																		Approve
																	</Menu.Item>
																	<Menu.Item
																		color="red"
																		leftSection={<IconX size={14} />}
																		onClick={(e) => {
																			e.stopPropagation();
																			if (user.authenticationUser?.id) {
																				rejectPremiumAccess({
																					variables: { userId: user.authenticationUser.id },
																				});
																			}
																		}}
																	>
																		Reject
																	</Menu.Item>
																</>
															)}
															{user.authenticationUser?.isPremium && (
																<Menu.Item
																	color="orange"
																	leftSection={<IconX size={14} />}
																	onClick={(e) => {
																		e.stopPropagation();
																		if (user.authenticationUser?.id) {
																			rejectPremiumAccess({
																				variables: { userId: user.authenticationUser.id },
																			});
																		}
																	}}
																>
																	Revoke Premium Access
																</Menu.Item>
															)}
															{!user.authenticationUser?.premiumRequestedAt && !user.authenticationUser?.isPremium && (
																<Menu.Item
																	color="green"
																	leftSection={<IconCheck size={14} />}
																	onClick={(e) => {
																		e.stopPropagation();
																		if (user.authenticationUser?.id) {
																			approvePremiumAccess({
																				variables: { userId: user.authenticationUser.id },
																			});
																		}
																	}}
																>
																	Grant Premium Access
																</Menu.Item>
															)}
															<Menu.Divider />
															<Menu.Label>User Management</Menu.Label>
															<Menu.Item
																leftSection={<IconKey size={14} />}
																onClick={(e) => {
																	e.stopPropagation();
																	if (user.authenticationUser?.id) {
																		bypassMigrated2FA({
																			variables: { userId: user.authenticationUser.id },
																		});
																	}
																}}
															>
																Bypass 2FA
															</Menu.Item>
															<Menu.Item
																color="red"
																leftSection={<IconTrash size={14} />}
																onClick={(e) => {
																	e.stopPropagation();
																	setDeletingId(user.id);
																	setDeleteModalOpen(true);
																}}
															>
																Delete User
															</Menu.Item>
														</Menu.Dropdown>
													</Menu>
												</Table.Td>
											</Table.Tr>
										))
									)}
								</Table.Tbody>
							</Table>
						</Table.ScrollContainer>
					</Paper>
				</DataContainer>

				{/* Delete Confirmation Modal */}
				<Modal
					opened={deleteModalOpen}
					onClose={() => {
						setDeleteModalOpen(false);
						setDeletingId(null);
					}}
					title="Confirm Delete"
					centered
				>
					<Stack>
						<Text>
							{deletingId
								? 'Are you sure you want to delete this user? This action cannot be undone.'
								: `Are you sure you want to delete ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}? This action cannot be undone.`}
						</Text>
						<Group justify="flex-end" gap="sm">
							<Button
								variant="default"
								onClick={() => {
									setDeleteModalOpen(false);
									setDeletingId(null);
								}}
							>
								Cancel
							</Button>
							<Button color="red" onClick={confirmDelete}>
								Delete
							</Button>
						</Group>
					</Stack>
				</Modal>

				{/* Pagination */}
				{data?.getAllUsers?.pageInfo && (
					<Group justify="space-between" align="center" mt="md">
						<Text size="sm" c="dimmed">
							Showing{' '}
							{Math.min(
								(currentPage - 1) * pageSize + 1,
								data.getAllUsers.pageInfo.totalCount
							)}{' '}
							-{' '}
							{Math.min(
								currentPage * pageSize,
								data.getAllUsers.pageInfo.totalCount
							)}{' '}
							of {data.getAllUsers.pageInfo.totalCount} items
						</Text>
						<Pagination
							value={currentPage}
							onChange={handlePageChange}
							total={Math.max(1, data.getAllUsers.pageInfo.totalPages)}
							size="sm"
						/>
					</Group>
				)}
			</Stack>
		</>
	);
}
